import { Mail, MessageSquare, Send, User } from 'lucide-react';
import { useState } from 'react';

import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { track } from '@/lib/mixpanel';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track the contact form submission as a Mixpanel event
    track('ContactFormSubmitted', {
      name,
      email,
      message,
      timestamp: new Date().toISOString(),
    });

    // Simulate a small delay for UX
    await new Promise((resolve) => setTimeout(resolve, 500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setName('');
    setEmail('');
    setMessage('');
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-800">
      <Navbar />

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-12 pt-28 sm:px-6 lg:px-8">
        <div className="rounded-lg bg-white p-8 shadow-sm">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            <h1 className="mb-2 font-heading text-3xl font-bold text-neutral-900">
              Contactează-ne
            </h1>
            <p className="text-neutral-600">
              Ai întrebări sau sugestii? Ne-ar plăcea să auzim de la tine.
            </p>
          </div>

          {isSubmitted ? (
            <div className="rounded-lg bg-green-50 p-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <Send className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-green-800">
                Mesaj trimis cu succes!
              </h2>
              <p className="mb-4 text-green-700">
                Îți mulțumim pentru mesaj. Te vom contacta în curând.
              </p>
              <Button
                variant="outline"
                onClick={() => setIsSubmitted(false)}
                className="border-green-300 text-green-700 hover:bg-green-100">
                Trimite alt mesaj
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-neutral-700">
                  Nume
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Numele tău"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-neutral-700">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@exemplu.com"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-medium text-neutral-700">
                  Mesaj
                </label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Scrie-ne mesajul tău aici..."
                  required
                  rows={5}
                  className="resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Se trimite...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Trimite mesajul
                  </span>
                )}
              </Button>
            </form>
          )}

          <div className="mt-8 border-t border-neutral-200 pt-6">
            <p className="text-center text-sm text-neutral-600">
              Poți să ne contactezi și direct la{' '}
              <a
                href="mailto:solomonq.team@gmail.com"
                className="font-medium text-primary hover:underline">
                solomonq.team@gmail.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
